import { ref, watch } from "vue";

/**
 * Shell navigation state, shared by TopBar (which owns the brand block and
 * both nav toggles) and Sidebar (which owns the drawer). A module-level
 * singleton rather than props: the two components are siblings under App.vue
 * and neither is the other's parent, so prop-drilling both toggles through
 * App would put layout state in three places instead of one.
 *
 * Two independent notions of "collapsed", because the two form factors want
 * different things:
 *  - `rail`   — DESKTOP. The 56px icon rail. Remembered across sessions.
 *  - `navOpen` — MOBILE. The temporary drawer over the content. Always starts
 *    closed; a remembered "open" would cover the page on every cold load.
 */
const RAIL_STORAGE_KEY = "pinksy:shell:railCollapsed";

function readRailPreference(): boolean {
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export const rail = ref(readRailPreference());
export const navOpen = ref(false);

watch(rail, (value) => {
  try {
    localStorage.setItem(RAIL_STORAGE_KEY, value ? "1" : "0");
  } catch {
    // best-effort — the preference just stops surviving a reload
  }
});

export function toggleRail(): void {
  rail.value = !rail.value;
}

export function toggleNav(): void {
  navOpen.value = !navOpen.value;
}

export function closeNav(): void {
  navOpen.value = false;
}

/** Test seam — resets the module singleton between mounts. */
export function resetNavState(): void {
  rail.value = false;
  navOpen.value = false;
}
